import { useState, useReducer, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { EyeIcon, EyeSlashIcon, DevicePhoneMobileIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { setAuthState, signOutUser } from '../store/authSlice'
import { authAPI, mfaAPI, supabase } from '../utils/supabase'
import { showInitialLoader } from '../utils/initialLoaderManager'

// Login Steps Enum
const LOGIN_STEPS = {
  PASSWORD: 'password',
  MFA_VERIFICATION: 'mfa_verification',
  COMPLETE: 'complete'
}

// State Reducer for simplified state management
const loginReducer = (state, action) => {
  try {
    switch (action.type) {
      case 'SET_EMAIL':
        return { ...state, email: action.payload, formErrors: { ...state.formErrors, email: '' }, error: null }
      case 'SET_PASSWORD':
        return { ...state, password: action.payload, formErrors: { ...state.formErrors, password: '' }, error: null }
      case 'SET_SHOW_PASSWORD':
        return { ...state, showPassword: !state.showPassword }
      case 'SET_FORM_ERRORS':
        return { ...state, formErrors: action.payload }
      case 'SET_LOADING':
        return { ...state, loading: action.payload }
      case 'SET_ERROR':
        return { ...state, error: action.payload, loading: false }
      case 'SET_LOGIN_STEP':
        return { ...state, loginStep: action.payload }
      case 'SET_MFA_CODE':
        return { ...state, mfaCode: action.payload.replace(/\D/g, '').slice(0, 6), error: action.payload.length > 0 && !state.isRateLimited ? null : state.error }
      case 'SET_MFA_FACTORS':
        return { ...state, mfaFactors: action.payload }
      case 'SET_TEMP_SESSION':
        return { ...state, tempSession: action.payload.session, tempUser: action.payload.user }
      case 'SET_RATE_LIMITED':
        return { ...state, isRateLimited: action.payload.isLimited, attemptCount: action.payload.count, error: action.payload.error || state.error }
      case 'RESET_FORM':
        return { ...state, loginStep: LOGIN_STEPS.PASSWORD, error: null, mfaCode: '', mfaFactors: [], attemptCount: 0, isRateLimited: false, tempSession: null, tempUser: null }
      default:
        return state
    }
  } catch (error) {
    console.error('Login form error:', error.message)
    return state
  }
}

// Rate limiting constants
const MAX_ATTEMPTS = 5
const RATE_LIMIT_DURATION = 300000 // 5 minutes in milliseconds

// Initial state
const initialState = {
  email: '',
  password: '',
  showPassword: false,
  formErrors: {},
  loading: false,
  error: null,
  loginStep: LOGIN_STEPS.PASSWORD,
  mfaCode: '',
  mfaFactors: [],
  attemptCount: 0,
  isRateLimited: false,
  tempSession: null,
  tempUser: null
}

// Main LoginForm Component - Seamless Flow
export default function LoginForm() {
  const [state, dispatch] = useReducer(loginReducer, initialState)
  const reduxDispatch = useDispatch()
  const navigate = useNavigate()

  // Check for existing rate limiting on mount
  useEffect(() => {
    checkRateLimit()
  }, [])

  // Rate limiting functions
  const checkRateLimit = () => {
    const rateLimitData = localStorage.getItem('mfa_rate_limit')
    if (rateLimitData) {
      const { count, timestamp } = JSON.parse(rateLimitData)
      const now = Date.now()
      
      if (now - timestamp < RATE_LIMIT_DURATION) {
        if (count >= MAX_ATTEMPTS) {
          dispatch({
            type: 'SET_RATE_LIMITED',
            payload: {
              isLimited: true,
              count,
              error: `Too many failed attempts. Please try again in ${Math.ceil((RATE_LIMIT_DURATION - (now - timestamp)) / 60000)} minutes.`
            }
          })
        } else {
          dispatch({
            type: 'SET_RATE_LIMITED',
            payload: { isLimited: false, count }
          })
        }
      } else {
        // Reset rate limiting
        localStorage.removeItem('mfa_rate_limit')
        dispatch({
          type: 'SET_RATE_LIMITED',
          payload: { isLimited: false, count: 0 }
        })
      }
    }
  }

  const updateRateLimit = (failed = false) => {
    const now = Date.now()
    const newCount = failed ? state.attemptCount + 1 : 0
    
    if (failed) {
      const isLimited = newCount >= MAX_ATTEMPTS
      
      dispatch({
        type: 'SET_RATE_LIMITED',
        payload: {
          isLimited,
          count: newCount,
          error: isLimited ? `Too many failed attempts. Please try again in ${Math.ceil(RATE_LIMIT_DURATION / 60000)} minutes.` : null
        }
      })
      
      localStorage.setItem('mfa_rate_limit', JSON.stringify({
        count: newCount,
        timestamp: now
      }))
    } else {
      // Success - clear rate limiting
      localStorage.removeItem('mfa_rate_limit')
      dispatch({
        type: 'SET_RATE_LIMITED',
        payload: { isLimited: false, count: 0 }
      })
    }
  }

  // Validation function
  const validateForm = () => {
    const errors = {}
    
    if (!state.email.trim()) {
      errors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(state.email)) {
      errors.email = 'Email is invalid'
    }

    if (!state.password) {
      errors.password = 'Password is required'
    } else if (state.password.length < 6) {
      errors.password = 'Password must be at least 6 characters'
    }

    dispatch({ type: 'SET_FORM_ERRORS', payload: errors })
    return Object.keys(errors).length === 0
  }

  // Password form submit handler - Simplified AAL-based flow
  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    dispatch({ type: 'SET_ERROR', payload: null })

    if (!validateForm()) {
      return
    }

    dispatch({ type: 'SET_LOADING', payload: true })

    try {
      // Step 1: Standard sign in with password
      const { data: authData, error: authError } = await authAPI.signIn(state.email, state.password)
      
      if (authError) {
        throw new Error(authError.message)
      }

      // Step 2: Check if user has MFA factors and current AAL level
      const { hasMFA } = await mfaAPI.checkUserMFAStatus()
      
      if (hasMFA) {
        // User has MFA enabled - get verified factors first
        const { factors } = await mfaAPI.getVerifiedFactors()
        
        if (factors.length > 0) {
          // User has verified MFA factors - check current AAL level
          const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
          
          // If user has MFA factors, we should always require AAL2
          if (aalData?.currentLevel !== 'aal2') {
            console.log('User has MFA enabled but session is not AAL2, requiring MFA verification')
            // MFA step-up required - don't set auth state yet
            dispatch({ type: 'SET_TEMP_SESSION', payload: { session: authData.session, user: authData.user } })
            dispatch({ type: 'SET_MFA_FACTORS', payload: factors })
            dispatch({ type: 'SET_LOADING', payload: false })
            dispatch({ type: 'SET_LOGIN_STEP', payload: LOGIN_STEPS.MFA_VERIFICATION })
            return
          }
        }
      }

      // No MFA required or already at AAL2 - complete login
      console.log('Completing login - no MFA required or already at AAL2')
      // Set auth state in Redux so ProtectedRoute knows user is authenticated
      reduxDispatch(setAuthState({ 
        session: authData.session, 
        user: authData.user 
      }))
      dispatch({ type: 'SET_LOGIN_STEP', payload: LOGIN_STEPS.COMPLETE })

      // Navigate to main app after successful login
      // Show loader before navigating so user sees progress during workspace load
      setTimeout(() => {
        showInitialLoader()
        navigate('/board')
      }, 500)
      
    } catch (err) {
      console.error('Authentication error:', err.message)
      dispatch({ type: 'SET_ERROR', payload: err.message })
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  // MFA form submit handler
  const handleMFASubmit = async (e) => {
    e.preventDefault()
    
    if (state.isRateLimited) {
      return
    }
    
    if (state.mfaCode.length !== 6) {
      dispatch({ type: 'SET_ERROR', payload: 'Please enter a 6-digit verification code' })
      return
    }

    if (state.mfaFactors.length === 0) {
      dispatch({ type: 'SET_ERROR', payload: 'No MFA factors available' })
      return
    }

    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    
    try {
      const factor = state.mfaFactors[0] // Use first available factor
      const factorId = factor.id
      
      // Create challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId
      })
      
      if (challengeError) throw challengeError
      
      // Verify challenge
      const { data: verifyData, error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: state.mfaCode
      })
      
      if (verifyError) {
        updateRateLimit(true) // Record failed attempt
        throw verifyError
      }
      
      // Success - clear rate limiting
      updateRateLimit(false)
      
      // Get updated session with AAL2
      const { data: sessionData } = await supabase.auth.getSession()
      if (sessionData.session) {
        // Complete login with full AAL2 session
        // Set auth state in Redux so ProtectedRoute knows user is authenticated
        reduxDispatch(setAuthState({ 
          session: sessionData.session, 
          user: sessionData.session.user 
        }))
        dispatch({ type: 'SET_LOGIN_STEP', payload: LOGIN_STEPS.COMPLETE })

        // Navigate to main app after successful MFA verification
        // Show loader before navigating so user sees progress during workspace load
        setTimeout(() => {
          showInitialLoader()
          navigate('/board')
        }, 500)
      } else {
        throw new Error('Authentication level not upgraded. Please try again.')
      }
      
    } catch (err) {
      const errorMessage = err.message || 'Verification failed. Please check your code and try again.'
      dispatch({ type: 'SET_ERROR', payload: errorMessage })
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  // Cancel login and return to password step
  const handleCancelLogin = async () => {
    try {
      // If we have a temp session, sign out to clean it up
      if (state.tempSession) {
        await authAPI.signOut()
      }
      // Reset form to initial state
      dispatch({ type: 'RESET_FORM' })
    } catch (error) {
      console.error('Error during cancel:', error)
      // Force reset even if logout fails
      dispatch({ type: 'RESET_FORM' })
    }
  }

  // Render based on current step
  const renderCurrentStep = () => {
    switch (state.loginStep) {
      case LOGIN_STEPS.PASSWORD:
        return (
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="space-y-2">
              <label htmlFor="email-address" className="block text-sm font-medium text-label-secondary">
                Email
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                className={`apple-input ${
                  state.formErrors.email ? 'apple-input-error' : ''
                }`}
                placeholder="Enter your email address"
                value={state.email}
                onChange={(e) => dispatch({ type: 'SET_EMAIL', payload: e.target.value })}
                disabled={state.loading}
              />
              {state.formErrors.email && (
                <p className="text-xs text-status-danger mt-1">{state.formErrors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-label-secondary">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={state.showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className={`apple-input pr-12 ${
                    state.formErrors.password ? 'apple-input-error' : ''
                  }`}
                  placeholder="Enter your password"
                  value={state.password}
                  onChange={(e) => dispatch({ type: 'SET_PASSWORD', payload: e.target.value })}
                  disabled={state.loading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  onClick={() => dispatch({ type: 'SET_SHOW_PASSWORD' })}
                  disabled={state.loading}
                >
                  {state.showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-text-muted hover:text-text-secondary transition-colors" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-text-muted hover:text-text-secondary transition-colors" />
                  )}
                </button>
              </div>
              {state.formErrors.password && (
                <p className="text-xs text-status-danger mt-1">{state.formErrors.password}</p>
              )}
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-accent-foreground hover:text-status-info font-medium transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={state.loading}
              className="apple-button-primary w-full flex items-center justify-center focus-apple disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {state.loading && (
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {state.loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        )

      case LOGIN_STEPS.MFA_VERIFICATION:
        return (
          <div className="space-y-6">
            {/* MFA Header */}
            <div className="text-center">
              <div className="w-12 h-12 bg-status-info rounded-full flex items-center justify-center mx-auto mb-4">
                <DevicePhoneMobileIcon className="w-6 h-6 text-white" />
              </div>
              <h2 className="apple-text-title text-xl">Multi-Factor Authentication</h2>
              <p className="apple-text-body text-text-muted mt-2">
                Enter the code from your authenticator app
              </p>
            </div>

            <form onSubmit={handleMFASubmit} className="space-y-6">
              {/* MFA Code Input */}
              <div className="space-y-2">
                <label htmlFor="mfa-code" className="block text-sm font-medium text-label-secondary">
                  Verification Code
                </label>
                <input
                  id="mfa-code"
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  className="apple-input text-center font-mono text-lg"
                  value={state.mfaCode}
                  onChange={(e) => dispatch({ type: 'SET_MFA_CODE', payload: e.target.value })}
                  autoComplete="one-time-code"
                  autoFocus
                  disabled={state.loading || state.isRateLimited}
                />
                <p className="apple-text-caption text-text-muted text-center">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>

              {/* Buttons */}
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={state.loading || state.mfaCode.length !== 6 || state.isRateLimited}
                  className="apple-button-primary w-full flex items-center justify-center focus-apple disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {state.loading && (
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {state.loading ? 'Verifying...' : 'Verify Code'}
                </button>
                
                <button
                  type="button"
                  onClick={handleCancelLogin}
                  className="apple-button-secondary w-full flex items-center justify-center"
                  disabled={state.loading}
                >
                  Cancel
                </button>
                
                {state.attemptCount > 0 && !state.isRateLimited && (
                  <p className="text-sm text-text-muted text-center">
                    {MAX_ATTEMPTS - state.attemptCount} attempts remaining
                  </p>
                )}
              </div>
            </form>
          </div>
        )

      case LOGIN_STEPS.COMPLETE:
        return (
          <div className="text-center space-y-4">
            <div className="w-12 h-12 bg-status-success rounded-full flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="apple-text-title text-xl">Welcome!</h2>
            <p className="apple-text-body text-text-muted">
              Redirecting to your dashboard...
            </p>
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4 py-8 font-system light-mode-only">
      <div className="w-full max-w-sm animate-fade-in">
        {/* Main Card */}
        <div className="apple-card animate-slide-in px-5 py-8">
          {/* Header - Only show for password step */}
          {state.loginStep === LOGIN_STEPS.PASSWORD && (
            <div className="text-center mb-3">
              <div className="w-75 h-auto flex items-center justify-center mx-auto mb-1">
                <div className="flex flex-col items-center">
                  <img
                      src="/taskrdo-mas.png"
                      alt="\tas-ker-doo\ Mascot"
                      className="h-24 w-auto object-contain"
                    />
                    <img
                      src="/taskrdo-label.png"
                      alt="\tas-ker-doo\ Label"
                      className="h-10 w-auto object-contain"
                    />
                </div>
              </div>
              <h1 className="apple-text-title mt-2 pt-4">Welcome back</h1>
              <p className="apple-text-body mb-2">Sign in to Get Sh·t Done</p>
            </div>
          )}

          {/* Error Message */}
          {state.error && (
            <div className="bg-accent-danger border border-status-danger rounded-lg p-3 mb-6">
              <p className="text-sm text-status-danger">{state.error}</p>
            </div>
          )}

          {/* Dynamic Content Based on Step */}
          {renderCurrentStep()}
        </div>

        {/* Sign Up Link - Only show for password step */}
        {state.loginStep === LOGIN_STEPS.PASSWORD && (
          <div className="text-center mt-6">
            <p className="apple-text-body">
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="text-accent-foreground hover:text-status-info font-medium transition-colors"
              >
                Sign up
              </Link>
            </p>
          </div>
        )}

        {/* Legal Links */}
        {state.loginStep === LOGIN_STEPS.PASSWORD && (
          <div className="text-center mt-4 text-xs text-text-muted">
            <Link to="/privacy" className="hover:text-text-secondary transition-colors">
              Privacy Policy
            </Link>
            {' · '}
            <Link to="/terms" className="hover:text-text-secondary transition-colors">
              Terms of Service
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
